import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { pathwayActions, usePathwayId } from '@/stores/pathwayStore';
import featureConfig from '@/featureConfig';
import usePathwayStore from '@/stores/pathwayStore';
import { confirm } from '@/components/core/createConfirmation';

const useClipboard = ({ isSelectionMode, setIsSelectionMode, reactFlow }) => {
  const clipboardRef = useRef(null);
  const selectedNodesRef = useRef([]);
  const lastPaneClickRef = useRef(null);
  const pathwayId = usePathwayId();

  // Clear clipboard and exit selection mode when pathway changes
  useEffect(() => {
    clipboardRef.current = null;
    selectedNodesRef.current = [];
    lastPaneClickRef.current = null;
    setIsSelectionMode(false);
  }, [pathwayId, setIsSelectionMode]);

  const onSelectionChange = useCallback(({ nodes }) => {
    selectedNodesRef.current = nodes || [];
  }, []);

  const onPaneClick = useCallback(
    event => {
      if (!reactFlow) return;
      const flowPosition = reactFlow.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      lastPaneClickRef.current = flowPosition;
    },
    [reactFlow],
  );

  const copySelection = useCallback(() => {
    const nodes = selectedNodesRef.current;
    if (!nodes.length) return;

    const clipboardNodes = nodes.map(n => ({
      originalId: n.id,
      name: n.data.name,
      type: n.type,
      label: n.data.label,
      position: { ...n.position },
      data: n.data,
    }));

    clipboardRef.current = {
      nodes: clipboardNodes,
    };

    // Reset click target on new copy so first paste uses viewport center
    lastPaneClickRef.current = null;

    toast.success(`Copied ${nodes.length} node(s)`);
  }, []);

  const deleteSelection = useCallback(async () => {
    const nodes = selectedNodesRef.current;
    if (!nodes.length) return;

    const { locked } = usePathwayStore.getState();
    if (locked) {
      toast.error('Cannot delete nodes in a locked pathway');
      return;
    }

    if (!featureConfig.manageModules) {
      toast.error('You don\t have permissions to perform this action');
      return;
    }

    const count = nodes.length;
    const confirmed = await confirm(`Are you sure you want to delete ${count} node(s)?`);
    if (!confirmed) return;

    for (const node of nodes) {
      await pathwayActions.removeNode(node.id);
    }

    selectedNodesRef.current = [];
    setIsSelectionMode(false);
    toast.success(`Deleted ${count} node(s)`);
  }, [setIsSelectionMode]);

  const pasteSelection = useCallback(async () => {
    if (!clipboardRef.current || !clipboardRef.current.nodes.length) return;

    const { locked } = usePathwayStore.getState();
    if (locked) {
      toast.error('Cannot paste into a locked pathway');
      return;
    }

    if (!featureConfig.manageModules) {
      toast.error('This feature is not available in the academic version');
      return;
    }

    await pathwayActions.pasteNodes(clipboardRef.current, lastPaneClickRef.current);
    setIsSelectionMode(false);
  }, [setIsSelectionMode]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = e => {
      // Skip if focused on input elements
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

      const isMod = e.metaKey || e.ctrlKey;

      if (isMod && e.key === 'c') {
        e.preventDefault();
        copySelection();
      }

      if (isMod && e.key === 'v') {
        e.preventDefault();
        pasteSelection();
      }

      if (isMod && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault();
        deleteSelection();
      }

      if (e.key === 'Escape' && isSelectionMode) {
        setIsSelectionMode(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [copySelection, pasteSelection, deleteSelection, isSelectionMode, setIsSelectionMode]);

  return { onSelectionChange, onPaneClick };
};

export default useClipboard;
